import { usePlayerAvatarUrl } from '../../avatar/usePlayerAvatarUrl';

/**
 * Player marker on the world map. Renders the player's avatar — a custom
 * uploaded image if they have one, otherwise their chosen commissioned face
 * (one of twelve) — at the requested size; falls back to the inline-SVG Foxy
 * mascot when no avatar resolves (e.g. asset folder missing in a trimmed dev
 * build).
 *
 * <p>When {@link AvatarProps#onPress} is supplied the avatar becomes a tappable
 * button (used on the world map to open the avatar picker); otherwise it's a
 * passive marker with {@code pointerEvents: 'none'} as before.
 *
 * <p>The Foxy SVG was the v0 placeholder before the commissioned avatars
 * landed — kept inline so the code path still works without the asset bundle.
 */
export interface AvatarProps {
  size?: number;
  /** When set, the avatar is tappable and fires this (opens the picker). */
  onPress?: () => void;
}

export function Avatar({ size = 56, onPress }: AvatarProps) {
  const url = usePlayerAvatarUrl('idle');
  const interactive = typeof onPress === 'function';

  const visual = url ? (
    <img
      src={url}
      alt=""
      draggable={false}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        objectFit: 'cover',
        display: 'block',
        userSelect: 'none',
        pointerEvents: 'none',
        // Subtle drop-shadow so the avatar reads against any biome
        // colour on the world-map background.
        filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.45))',
      }}
    />
  ) : (
    <FoxyFallback size={size} />
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onPress}
        aria-label="Change your avatar"
        style={{
          padding: 0,
          border: 'none',
          background: 'transparent',
          borderRadius: '50%',
          cursor: 'pointer',
          display: 'block',
          pointerEvents: 'auto',
          lineHeight: 0,
        }}
      >
        {visual}
      </button>
    );
  }
  return visual;
}

function FoxyFallback({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        <radialGradient id="foxy-face" cx="0.4" cy="0.35" r="0.7">
          <stop offset="0%" stopColor="#FFE3C4" />
          <stop offset="55%" stopColor="#F4B07A" />
          <stop offset="100%" stopColor="#C57A47" />
        </radialGradient>
        <radialGradient id="foxy-hair" cx="0.5" cy="0.3" r="0.7">
          <stop offset="0%" stopColor="#FFD089" />
          <stop offset="100%" stopColor="#E0913A" />
        </radialGradient>
      </defs>

      {/* shadow */}
      <ellipse cx="50" cy="92" rx="22" ry="3.5" fill="#000" opacity="0.22" />

      {/* head */}
      <ellipse cx="50" cy="50" rx="32" ry="28" fill="url(#foxy-face)" />

      {/* ears */}
      <path d="M22 38 Q16 12 36 28 Q32 34 28 42 Z" fill="url(#foxy-face)" />
      <path d="M25 32 Q22 22 32 28 Q31 32 28 36 Z" fill="#F19BB0" opacity="0.85" />
      <path d="M78 38 Q84 12 64 28 Q68 34 72 42 Z" fill="url(#foxy-face)" />
      <path d="M75 32 Q78 22 68 28 Q69 32 72 36 Z" fill="#F19BB0" opacity="0.85" />

      {/* hair tuft */}
      <path
        d="M36 28 Q44 14 50 22 Q56 14 64 28 Q56 26 50 28 Q44 26 36 28 Z"
        fill="url(#foxy-hair)"
      />

      {/* face shading wash */}
      <ellipse cx="50" cy="56" rx="26" ry="16" fill="#fff" opacity="0.18" />

      {/* cheeks */}
      <ellipse cx="36" cy="60" rx="5" ry="3" fill="#F19BB0" opacity="0.7" />
      <ellipse cx="64" cy="60" rx="5" ry="3" fill="#F19BB0" opacity="0.7" />

      {/* eyes */}
      <ellipse cx="40" cy="50" rx="4.5" ry="5" fill="#1A1424" />
      <ellipse cx="60" cy="50" rx="4.5" ry="5" fill="#1A1424" />
      <ellipse cx="38.5" cy="48" rx="2" ry="2.5" fill="#fff" />
      <ellipse cx="58.5" cy="48" rx="2" ry="2.5" fill="#fff" />

      {/* nose */}
      <ellipse cx="50" cy="60" rx="2" ry="1.5" fill="#3A2218" />

      {/* mouth */}
      <path
        d="M46 64 Q50 67 54 64"
        stroke="#3A2218"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}
